import {
  Form,
  RadioGroupField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { UserFormSchema } from '@/features/tenant-manager/user/_models/user';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof UserFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
  isEditing?: boolean;
};

export const UserForm: FC<Props> = ({ initialValues, onSubmit, isEditing }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(UserFormSchema),
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <TextInputField
        name="name"
        autocompleteKey="user.name"
        label={t('name')}
        control={control}
        errors={errors}
        required
      />
      <TextInputField
        name="email"
        autocompleteKey="user.email"
        label={t('email')}
        control={control}
        errors={errors}
        required
      />
      {isEditing && (
        <RadioGroupField
          name="isEmailNotificationEnabled"
          label={t('email-notification-for-comment-and-workflow')}
          control={control}
          errors={errors}
          options={[
            {
              label: t('email-notification-non-target'),
              value: false,
            },
            {
              label: t('email-notification-target'),
              value: true,
            },
          ]}
          required
        />
      )}
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
